import { useCallback } from 'react'

// Cooldown between showings of the v3→v2 opt-out survey. Persisted in
// localStorage (per-browser) — the opt-out is a self-service browser-local
// toggle, so localStorage is the right granularity. Also avoids requiring a
// new RsatPulseSurveySlugEnum value on the backend.
export const NAV_V3_OPT_OUT_SURVEY_COOLDOWN_MS = 24 * 60 * 60 * 1000
export const NAV_V3_OPT_OUT_SURVEY_STORAGE_KEY = 'navV3OptOutSurvey'
export const NAV_V3_OPT_OUT_SURVEY_MILESTONE_SLUG = 'nav_v3_opt_out_r2026_05'

type NavV3OptOutSurveyStorage = {
  lastSeenTime: number
}

const readStorage = (): NavV3OptOutSurveyStorage | null => {
  try {
    const raw = window.localStorage.getItem(NAV_V3_OPT_OUT_SURVEY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as NavV3OptOutSurveyStorage
    return typeof parsed?.lastSeenTime === 'number' ? parsed : null
  } catch {
    return null
  }
}

const writeStorage = (value: NavV3OptOutSurveyStorage) => {
  try {
    window.localStorage.setItem(NAV_V3_OPT_OUT_SURVEY_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // localStorage can throw in private browsing / quota-exceeded scenarios.
    // Failing silently is fine: worst case we re-show the modal next switch.
  }
}

// Returns a function that resolves true when the modal may open right now.
// Reads localStorage on every call so the latest value (e.g. written from
// another tab) is honored without needing a re-render.
export const useShouldOpenNavV3OptOutSurvey = () =>
  useCallback(() => {
    const lastSeenTime = readStorage()?.lastSeenTime
    if (lastSeenTime == null) return true
    return Date.now() - lastSeenTime >= NAV_V3_OPT_OUT_SURVEY_COOLDOWN_MS
  }, [])

// Marks the survey as seen now so the cooldown starts. Called when the modal
// opens — not on close — so a user who immediately dismisses still triggers
// the 24h backoff.
export const useMarkNavV3OptOutSurveySeen = () =>
  useCallback(() => {
    writeStorage({ lastSeenTime: Date.now() })
  }, [])
